import {
  ColumnLayout,
  DatePicker,
  FormField,
  Input,
  Select,
  SpaceBetween,
  TimeInput,
  Toggle,
} from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MeridiemOptions } from '../../../constants/DateTimeConstants';
import { EditEventActions, useEditEvent } from '../../../store/edit-event.context';
import { useUser } from '../../../store/user.context';
import { Campaign } from '../../../types/Campaign';
import { ChallengeDescriptor, ChallengeDifficulty, ChallengeListItem } from '../../../types/Challenge';
import { Nullable } from '../../../types/common';
import { Event } from '../../../types/Event';
import { getBrowserTimezoneName, polarisDateTimeToMoment } from '../../../utils/event-time.utils';
import { i18nKeys } from '../../../utils/i18n.utils';
import { getLanguageCodeSafe } from '../../../utils/locale.utils';
import { ConfirmModal } from '../ConfirmModal';
import { HorizontalRule } from '../HorizontalRule';
import { KeyValue } from '../KeyValue';
import { isDateNotInThePast } from '@/src/utils/common.utils';
import useInputBuffer from '@/src/hooks/useInputBuffer';
import moment from 'moment';

interface ChallengeCardAdvancedProps {
  challenge: ChallengeListItem;
  getDefaultChallengePointsPossible: (challenge: ChallengeListItem) => number;
  target: Event | Campaign;
  canEditWithAttribute: (attribute: string) => boolean | null;
  challengeDescriptor: Nullable<ChallengeDescriptor>;
  handleEditEvent: (newChallengeDescriptor: ChallengeDescriptor) => void;
}

const getTimeSectionsFromDescriptor = (ch: ChallengeDescriptor | null | undefined) => {
  if (ch?.displayTime) {
    const m = moment(ch.displayTime);
    if (m.isValid()) {
      return {
        date: m.format('YYYY-MM-DD'),
        time: m.format('HH:mm'),
        meridiem: m.format('A'),
      };
    }
  }
  return {
    date: '',
    time: '',
    meridiem: '',
  };
};

const KeyValueDescription: React.FC = ({ children }) => (
  <small style={{ minHeight: '4.5rem', display: 'inline-block' }}>{children}</small>
);

const ChallengeCardAdvanced: React.FC<ChallengeCardAdvancedProps> = ({
  challenge,
  getDefaultChallengePointsPossible,
  target,
  canEditWithAttribute,
  challengeDescriptor,
  handleEditEvent,
}) => {
  const { i18n, t } = useTranslation();
  const challengeDifficultyOptions: { label: string; value: string }[] = useMemo(() => {
    return ChallengeDifficulty.values.map((challengeDifficulty) => ({
      label: t(challengeDifficulty.i18nKeyShort),
      value: challengeDifficulty.key.toString(),
    }));
  }, []);
  const [selectedMeridiem, setSelectedMeridiem] = useState<OptionDefinition>(
    getTimeSectionsFromDescriptor(challengeDescriptor).meridiem === 'PM' ? MeridiemOptions.PM : MeridiemOptions.AM
  );
  const { handleUpdateEditEvent, editMode, editedEvent } = useEditEvent();
  const { user } = useUser();
  const [selectedDifficulty, setSelectedDifficulty] = useState<Nullable<OptionDefinition>>(
    () =>
      challengeDifficultyOptions.find(
        (difficultyOption: OptionDefinition) =>
          Number(difficultyOption.value) === (challengeDescriptor?.overrides?.difficulty ?? challenge.props.difficulty)
      ) || null
  );
  const [selectedPointsPossible, setSelectedPointsPossible] = useState(
    challengeDescriptor?.overrides.score ?? getDefaultChallengePointsPossible(challenge)
  );
  const [autoScalingOverride, setAutoScalingOverride] = useState(challengeDescriptor?.labAutoScalingOverride ?? 0);
  const [restartsAllowed, setRestartsAllowed] = useState(challengeDescriptor?.restartsAllowed ?? 0);
  const [selectedDate, setSelectedDate] = useState(getTimeSectionsFromDescriptor(challengeDescriptor).date);
  const [selectedTime, setSelectedTime] = useState(getTimeSectionsFromDescriptor(challengeDescriptor).time);
  const [startTimeError, setStartTimeError] = useState('');
  const [cliAccessWarningVisible, setCliAccessWarningVisible] = useState(false);
  const isEvent = target instanceof Event;

  const languageCode: string = getLanguageCodeSafe(i18n.language);

  useEffect(() => {
    if (editMode) {
      handleAddToEvent();
    }
  }, [challengeDescriptor]);

  useEffect(() => {
    if (editMode) {
      handleDateTimeChange();
    }
  }, [selectedDate, selectedTime, selectedMeridiem]);

  const toggleWarmup = () => {
    const modifiedDescriptor = challengeDescriptor;
    if (modifiedDescriptor) {
      modifiedDescriptor.overrides.challengeAlwaysOn = !challengeDescriptor?.overrides.challengeAlwaysOn;
      modifiedDescriptor.setDateTimeModels();
      handleEditEvent(modifiedDescriptor);
    }
  };

  const toggleVirtualQueue = () => {
    const modifiedDescriptor = challengeDescriptor;
    if (modifiedDescriptor) {
      modifiedDescriptor.virtualQueueSettings.enabled = !challengeDescriptor.virtualQueueSettings.enabled;
      handleEditEvent(modifiedDescriptor);
    }
  };

  const toggleHidden = () => {
    const modifiedDescriptor = challengeDescriptor;
    if (modifiedDescriptor) {
      modifiedDescriptor.hidden = !challengeDescriptor?.hidden;
      if (modifiedDescriptor.hidden) {
        modifiedDescriptor.displayTime = null;
      }
      modifiedDescriptor.setDateTimeModels();
      handleEditEvent(modifiedDescriptor);
    }
  };

  const setCliAccessDisabled = (cliAccessDisabled: boolean) => {
    const modifiedDescriptor = challengeDescriptor;
    if (modifiedDescriptor) {
      modifiedDescriptor.cliAccessDisabled = cliAccessDisabled;
      handleEditEvent(modifiedDescriptor);
    }
  };

  // Turning CLI access off needs the owner to confirm the warning first; turning it back on applies right away.
  const toggleCliAccessDisabled = () => {
    if (challengeDescriptor?.cliAccessDisabled) {
      setCliAccessDisabled(false);
    } else {
      setCliAccessWarningVisible(true);
    }
  };

  const confirmDisableCliAccess = () => {
    setCliAccessDisabled(true);
    setCliAccessWarningVisible(false);
  };

  const handleScoreChange = (score: string) => {
    const newScore = Number(score);
    if (Number.isNaN(newScore)) {
      return;
    }
    setSelectedPointsPossible(newScore);
    const modifiedDescriptor = challengeDescriptor;
    if (modifiedDescriptor) {
      modifiedDescriptor.overrides.score = newScore;
      handleEditEvent(modifiedDescriptor);
    }
  };

  const handleAutoScalingOverrideChange = (autoScaleOveride: string) => {
    const autoScaleOverideNumber = Number(autoScaleOveride);
    if (Number.isNaN(autoScaleOverideNumber)) {
      return;
    }
    setAutoScalingOverride(autoScaleOverideNumber);
    const modifiedDescriptor = challengeDescriptor;
    if (modifiedDescriptor) {
      modifiedDescriptor.labAutoScalingOverride = autoScaleOverideNumber;
      handleEditEvent(modifiedDescriptor);
    }
  };

  const handleRestartsAllowedChange = (selectedRestartsAllowed: string) => {
    const restartsAllowedNumber = Number(selectedRestartsAllowed);
    if (restartsAllowedNumber > 0 && restartsAllowedNumber < 6) {
      setRestartsAllowed(restartsAllowedNumber);
      const modifiedDescriptor = challengeDescriptor;
      if (modifiedDescriptor) {
        modifiedDescriptor.restartsAllowed = restartsAllowedNumber;
        handleEditEvent(modifiedDescriptor);
      }
    }
  };

  const handleDifficultyChange = (selectedDifficultyOption: OptionDefinition) => {
    setSelectedDifficulty(selectedDifficultyOption);
    const modifiedDescriptor = challengeDescriptor;
    if (modifiedDescriptor) {
      modifiedDescriptor.overrides.difficulty = Number(selectedDifficultyOption.value);
      handleEditEvent(modifiedDescriptor);
    }
  };

  const handleAddToEvent = () => {
    const newChallengeDescriptors = target?.challengeDescriptors ?? [];
    const challengeDescriptorIndex = newChallengeDescriptors?.findIndex(
      (cd: ChallengeDescriptor) => cd.challengeId === challengeDescriptor?.challengeId
    );
    if (challengeDescriptor?.displayTime?.includes('NaN')) {
      challengeDescriptor.displayTime = null;
    }
    if (challengeDescriptorIndex && challengeDescriptor) {
      newChallengeDescriptors[challengeDescriptorIndex] = challengeDescriptor;
      handleUpdateEditEvent(EditEventActions.CHALLENGE_DESCRIPTORS, newChallengeDescriptors);
    }
  };

  const handleDateTimeChange = () => {
    if (isEvent) {
      const newDisplayTime = polarisDateTimeToMoment(
        selectedDate,
        `${selectedTime} ${selectedMeridiem.value}`,
        target?.timezone ?? getBrowserTimezoneName() ?? ''
      );
      const modifiedDescriptor = challengeDescriptor;
      if (modifiedDescriptor && newDisplayTime) {
        modifiedDescriptor.displayTime = newDisplayTime.format();
        handleEditEvent(modifiedDescriptor);
      }
    }
  };

  const handleValidation = () => {
    const startDate: Date = new Date(editedEvent?.startDate ?? '');
    const selectedValue: Date = new Date(`${selectedDate} ${selectedTime} ${selectedMeridiem.label}`);

    if (selectedValue && startDate?.getTime() > selectedValue.getTime()) {
      setStartTimeError(t(i18nKeys.challenges.challengeSelection.advancedSettings.labels.startTimeError));
    } else {
      setStartTimeError('');
    }
  };

  // commitOnChange so the native number steppers update the model immediately
  const pointsPossibleInput = useInputBuffer(selectedPointsPossible, handleScoreChange, {
    snapBackOnEmpty: true,
    commitOnChange: true,
  });
  const autoScalingInput = useInputBuffer(autoScalingOverride, handleAutoScalingOverrideChange, {
    snapBackOnEmpty: true,
    commitOnChange: true,
  });
  const restartsInput = useInputBuffer(restartsAllowed, handleRestartsAllowedChange, {
    snapBackOnEmpty: true,
    commitOnChange: true,
  });

  /**
   * TODO:
   * Align with/implement Feature Flags
   */
  return user ? (
    <div style={{ marginTop: '4px' }}>
      <HorizontalRule />
      <h3>{t(i18nKeys.challenges.challengeSelection.advancedSettings.header)}</h3>
      <ColumnLayout columns={3}>
        <Toggle
          data-testid="challenge-advanced-warmup-toggle"
          disabled={!canEditWithAttribute('challengeAlwaysOn')}
          checked={!!challengeDescriptor?.overrides.challengeAlwaysOn}
          onChange={toggleWarmup}
        >
          {t(i18nKeys.challenges.challengeSelection.advancedSettings.labels.warmup, {
            warmup: challenge?.props.challengeAlwaysOn ? t(i18nKeys.general.yes) : t(i18nKeys.general.no),
          })}
        </Toggle>
        {isEvent && (
          <Toggle
            data-testid="challenge-advanced-hidden-toggle"
            disabled={!canEditWithAttribute('hidden')}
            checked={!!challengeDescriptor?.hidden}
            onChange={toggleHidden}
          >
            {t(i18nKeys.challenges.challengeSelection.advancedSettings.labels.locked)}
          </Toggle>
        )}
        {isEvent && (
          <Toggle
            data-testid="challenge-advanced-cli-access-disabled-toggle"
            disabled={!canEditWithAttribute('cliAccessDisabled')}
            checked={!!challengeDescriptor?.cliAccessDisabled}
            onChange={toggleCliAccessDisabled}
          >
            {t(i18nKeys.challenges.challengeSelection.advancedSettings.labels.disableCliAccess)}
          </Toggle>
        )}
        <Toggle
          data-testid="challenge-advanced-virtual-queue-toggle"
          disabled={!canEditWithAttribute('virtualQueueSettings')}
          checked={!!challengeDescriptor?.virtualQueueSettings.enabled}
          onChange={toggleVirtualQueue}
        >
          {t(i18nKeys.challenges.challengeSelection.advancedSettings.labels.virtualQueueEnabled)}
        </Toggle>
        {isEvent && (
          <KeyValue
            label={t(i18nKeys.challenges.challengeSelection.advancedSettings.labels.unlockTime)}
            description={
              <KeyValueDescription>
                {t(i18nKeys.challenges.challengeSelection.advancedSettings.descriptions.unlockTime)}
              </KeyValueDescription>
            }
            alignWithDescriptiveKeyValues
          >
            <FormField i18nStrings={{ errorIconAriaLabel: t(i18nKeys.general.error) }} errorText={startTimeError}>
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <DatePicker
                  data-testid="challenge-advanced-date-picker"
                  disabled={!canEditWithAttribute('displayTime')}
                  className="float-left inline"
                  placeholder="YYYY/MM/DD"
                  onChange={({ detail }) => setSelectedDate(detail.value)}
                  value={selectedDate}
                  i18nStrings={{
                    openCalendarAriaLabel: (newSelectedDate) =>
                      t(i18nKeys.general.chooseDate) +
                      (newSelectedDate ? t(i18nKeys.general.selectedDate, { selectedDate: newSelectedDate }) : ''),
                    previousMonthAriaLabel: t(i18nKeys.events.fields.filters.labels.previousMonth),
                    nextMonthAriaLabel: t(i18nKeys.events.fields.filters.labels.nextMonth),
                    todayAriaLabel: t(i18nKeys.events.fields.filters.labels.today),
                  }}
                  locale={languageCode}
                  isDateEnabled={(e) => isDateNotInThePast(e, target?.startDate ?? '')}
                  onBlur={handleValidation}
                />
                <TimeInput
                  data-testid="challenge-advanced-time-input"
                  disabled={!canEditWithAttribute('displayTime')}
                  className="time-input-small inline ml-5 float-left"
                  placeholder="hh:mm"
                  format="hh:mm"
                  onChange={({ detail }) => setSelectedTime(detail.value)}
                  onBlur={handleValidation}
                  value={selectedTime}
                />
                <Select
                  data-testid="challenge-advanced-time-select-am-pm"
                  disabled={!canEditWithAttribute('displayTime')}
                  className="float-left force-overlap"
                  options={[MeridiemOptions.AM, MeridiemOptions.PM]}
                  onChange={({ detail }) => setSelectedMeridiem(detail.selectedOption)}
                  selectedOption={selectedMeridiem}
                  onBlur={handleValidation}
                />
              </SpaceBetween>
            </FormField>
          </KeyValue>
        )}
        <KeyValue
          label={t(i18nKeys.challenges.challengeSelection.advancedSettings.labels.difficulty)}
          description={
            <KeyValueDescription>
              {t(i18nKeys.challenges.challengeSelection.advancedSettings.descriptions.difficulty, {
                difficulty: t(ChallengeDifficulty.getByKey(challenge.props.difficulty)?.i18nKeyShort),
              })}
            </KeyValueDescription>
          }
          alignWithDescriptiveKeyValues
        >
          <Select
            data-testid="challenge-advanced-difficulty-select"
            disabled={!canEditWithAttribute('challengeDifficultyOverride')}
            placeholder={t(i18nKeys.general.chooseAnOption)}
            options={challengeDifficultyOptions}
            onChange={({ detail }) => handleDifficultyChange(detail.selectedOption)}
            selectedOption={selectedDifficulty}
          />
        </KeyValue>
        <KeyValue
          label={t(i18nKeys.challenges.challengeSelection.advancedSettings.labels.pointsPossible)}
          description={
            <KeyValueDescription>
              {t(i18nKeys.challenges.challengeSelection.advancedSettings.descriptions.pointsPossible, {
                pointsPossible: getDefaultChallengePointsPossible(challenge),
              })}
            </KeyValueDescription>
          }
          alignWithDescriptiveKeyValues
        >
          <Input
            data-testid="challenge-advanced-points-possible"
            disabled={!canEditWithAttribute('pointsPossibleOverride')}
            value={pointsPossibleInput.value}
            onChange={({ detail }) => pointsPossibleInput.handleChange(detail.value)}
            onBlur={pointsPossibleInput.handleBlur}
            type="number"
          />
        </KeyValue>
        {isEvent && (
          <KeyValue
            label={t(i18nKeys.challenges.challengeSelection.advancedSettings.labels.autoscalingOverride)}
            description={
              <KeyValueDescription>
                {t(i18nKeys.challenges.challengeSelection.advancedSettings.descriptions.autoscalingOverride)}
              </KeyValueDescription>
            }
            alignWithDescriptiveKeyValues
          >
            <Input
              data-testid="challenge-advanced-autoscaling-override"
              disabled={!canEditWithAttribute('autoScalingOverride')}
              type="number"
              value={autoScalingInput.value}
              onChange={({ detail }) => autoScalingInput.handleChange(detail.value)}
              onBlur={autoScalingInput.handleBlur}
            />
          </KeyValue>
        )}
        <KeyValue
          label={t(i18nKeys.challenges.challengeSelection.advancedSettings.labels.restartsAllowed)}
          description={
            <KeyValueDescription>
              {t(i18nKeys.challenges.challengeSelection.advancedSettings.descriptions.restartsAllowed, {
                restartsAllowed: 0,
              })}
            </KeyValueDescription>
          }
          alignWithDescriptiveKeyValues
        >
          <Input
            data-testid="challenge-advanced-restarts-override"
            disabled={!canEditWithAttribute('restartsOverride')}
            type="number"
            value={restartsInput.value}
            onChange={({ detail }) => restartsInput.handleChange(detail.value)}
            onBlur={restartsInput.handleBlur}
          />
        </KeyValue>
      </ColumnLayout>
      {isEvent && (
        <ConfirmModal
          data-testid="challenge-advanced-cli-access-warning-modal"
          visible={cliAccessWarningVisible}
          title={t(i18nKeys.challenges.challengeSelection.advancedSettings.disableCliAccessWarning.title)}
          message={t(i18nKeys.challenges.challengeSelection.advancedSettings.disableCliAccessWarning.message)}
          confirmBtnLabel={t(i18nKeys.challenges.challengeSelection.advancedSettings.disableCliAccessWarning.confirm)}
          onConfirm={confirmDisableCliAccess}
          onCancel={() => setCliAccessWarningVisible(false)}
          cancelButtonId="challenge-advanced-cli-access-warning-cancel"
          submitButtonId="challenge-advanced-cli-access-warning-confirm"
        />
      )}
    </div>
  ) : null;
};

export default ChallengeCardAdvanced;
